import React from "react";

import {
  Experiment,
  FromProduct,
  SOURCE_TYPE_PRODUCT_PAGE_PERSONALISED_RECOMMENDER,
  SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_EVENTS_V2,
} from "@catchoftheday/analytics";
import { Box } from "@catchoftheday/cg-components";
import { styled, themeGet } from "@catchoftheday/theme";
import { isSoldByCatch } from "@catchoftheday/utilities";

import {
  PERSONALISED_PRODUCT_RECOMMENDER_EXPERIMENT,
  SIMILAR_PRODUCT_RECOMMENDER_EXPERIMENT,
} from "@personalisation/consts";

import { BrandRecommendedProducts } from "../BrandRecommendedProducts";
import { PeopleAlsoViewed } from "../PeopleAlsoViewed";
import { PersonalisedProductRecommender } from "../PersonalisedProductRecommender";
import { RecommendedEvents } from "../RecommendedEvents";
import { SimilarProducts } from "../SimilarProducts";
import {
  showRecommenderForBrandSlug,
  showRecommenderForSellerId,
} from "../utils";

const RecommenderWrapper = styled(Box)`
  > div:nth-of-type(even) {
    background: ${themeGet("ui.backgroundColor")};
  }
  > div:nth-of-type(odd) {
    background: white;
  }
  clear: both;
  overflow: hidden;
`;

export type RecommenderContainerProps = {
  brand?: {
    name: string;
    slug: string;
  };
  offer?: {
    seller: {
      id: string;
      name: string;
    };
  } | null;
  productId: string;
  adult: boolean;
  fromProduct: FromProduct;
  sourceId?: string;
};

export const RecommenderContainer = ({
  brand,
  offer,
  productId,
  adult,
  fromProduct,
  sourceId,
}: RecommenderContainerProps) => {
  const isBrandRestrictedRecommender =
    brand &&
    isSoldByCatch(offer?.seller?.id) &&
    showRecommenderForBrandSlug(brand.slug);
  const isSellerRestrictedRecommender =
    offer?.seller && showRecommenderForSellerId(offer?.seller?.id);
  const isRestrictedRecommender =
    isBrandRestrictedRecommender || isSellerRestrictedRecommender;

  return (
    <RecommenderWrapper>
      {isRestrictedRecommender && brand && (
        <BrandRecommendedProducts
          productIds={[productId]}
          brandName={brand.name}
          sellerName={
            isSellerRestrictedRecommender ? offer?.seller?.name : undefined
          }
          fromProduct={fromProduct}
          sourceId={sourceId}
        />
      )}
      {!isRestrictedRecommender && (
        <>
          <PeopleAlsoViewed
            productIds={[productId]}
            adult={adult}
            fromProduct={fromProduct}
            sourceId={sourceId}
          />
          <Experiment experiment={SIMILAR_PRODUCT_RECOMMENDER_EXPERIMENT}>
            {(exp) =>
              exp?.active && (
                <SimilarProducts
                  productId={productId}
                  adult={adult}
                  fromProduct={fromProduct}
                  experimentBucket={exp.bucket}
                  sourceId={sourceId}
                />
              )
            }
          </Experiment>
          <RecommendedEvents
            productIds={[productId]}
            sourceType={SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_EVENTS_V2}
            fromProduct={fromProduct}
            sourceId={sourceId}
          />
          <Experiment experiment={PERSONALISED_PRODUCT_RECOMMENDER_EXPERIMENT}>
            {(exp) =>
              exp?.bucket === 1 ? (
                <PersonalisedProductRecommender
                  adult={adult}
                  fromProduct={fromProduct}
                  sourceId={sourceId}
                  sourceType={SOURCE_TYPE_PRODUCT_PAGE_PERSONALISED_RECOMMENDER}
                />
              ) : null
            }
          </Experiment>
        </>
      )}
    </RecommenderWrapper>
  );
};
